import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { SIDEBAR_SHORTCUT_LABEL } from '@/lib/platform';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, type LucideIcon, PanelLeftClose } from 'lucide-react';
import { type ReactNode, useState } from 'react';

export interface SidebarItem {
  label: string;
  /** 경로. onClick 과 배타적 — 둘 중 하나만 준다. */
  to?: string;
  /** 클릭 핸들러 (예: 모달 오픈). 제공되면 `<Link>` 대신 `<button>` 으로 렌더. */
  onClick?: () => void;
  icon?: LucideIcon;
  badge?: string;
  /** 항목 우측에 작은 키 표기 (예: '⌘K'). action 과 같이 쓸 수 있다. */
  shortcut?: string;
  /** 항목 우측에 호버 시 노출될 액션 노드 (예: kebab 메뉴 버튼). Link 바깥 sibling 으로 렌더됨. */
  action?: ReactNode;
  /** 제공되면 `<Link>` 대신 이 노드로 아이템을 렌더 (예: 인라인 rename input). */
  renderOverride?: ReactNode;
}

export interface SidebarGroup {
  /** 그룹 헤더 (제공되면 별도 섹션 헤더로 렌더). */
  heading?: string;
  /** 펼침/접힘 가능한 그룹. heading 옆에 chevron 표시. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** true 면 이 그룹의 **items 만** 별도 스크롤 영역에 렌더. heading 은 상단 고정 영역에 남는다. 보통 마지막 그룹에만 지정한다 (예: 채팅 목록). */
  scrollable?: boolean;
  /** heading 우측 inline 액션 (예: `+` 버튼). */
  headingAction?: ReactNode;
  items: SidebarItem[];
  /** 제공되면 items 대신 이 노드를 렌더 (chat 프로젝트 섹션처럼 자체 트리 구조가 있는 그룹). */
  renderContent?: ReactNode;
}

interface WorkspaceProps {
  name: string;
  /** 클릭 가능 여부 (단일 워크스페이스면 false). */
  selectable?: boolean;
}

interface AppSidebarProps {
  /** 상단 워드마크 좌측 표시 — 기본값 'innermate'. */
  brand?: string;
  /** 상단 로고 이미지 URL — 기본값 '/logo.png'. 어드민이 브랜딩 설정 시 사용자 화면에서만 override. */
  logoSrc?: string;
  workspace?: WorkspaceProps;
  groups: SidebarGroup[];
  activePath: string;
  width?: 240 | 256;
  /** 사이드바 푸터 위에 추가될 보조 링크 (예: 문서). */
  /** 외부 URL 은 `href`, 내부 라우트는 `to` 로 지정 (둘 중 하나만). */
  utilityLinks?: { label: string; icon: LucideIcon; href?: string; to?: string }[];
  footer?: ReactNode;
  /** 제공되면 brand 우측에 사이드바 닫기 버튼 노출. */
  onClose?: () => void;
}

export function AppSidebar({
  brand = 'innermate',
  logoSrc = '/logo.png',
  workspace,
  groups,
  activePath,
  width = 240,
  utilityLinks,
  footer,
  onClose,
}: AppSidebarProps) {
  const widthClass = width === 256 ? 'w-64' : 'w-60';

  // scrollable 그룹을 기준으로 사이드바를 상단 고정 / 하단 스크롤 두 영역으로 나눈다.
  // scrollable 그룹의 heading 은 상단 고정 영역에 남고, 그 items 만 스크롤된다.
  const scrollableIdx = groups.findIndex((g) => g.scrollable);
  const hasScrollable = scrollableIdx >= 0;
  const fixedGroups = hasScrollable ? groups.slice(0, scrollableIdx) : groups;
  const scrollableGroup = hasScrollable ? groups[scrollableIdx] : null;

  return (
    <aside
      className={cn(widthClass, 'shrink-0 border-r border-border bg-background flex flex-col')}
    >
      <div className="px-4 h-14 flex items-center gap-2 shrink-0 border-b border-border">
        <img src={logoSrc} alt={brand} className="size-8 rounded-md object-contain" />
        <span className="text-sm font-semibold tracking-tight flex-1 truncate">{brand}</span>
        {onClose ? (
          <Tooltip label="사이드바 숨기기" shortcut={SIDEBAR_SHORTCUT_LABEL} side="bottom">
            <button
              type="button"
              onClick={onClose}
              aria-label="사이드바 숨기기"
              className="size-7 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors"
            >
              <PanelLeftClose className="size-4" />
            </button>
          </Tooltip>
        ) : null}
      </div>

      {workspace ? (
        <div className="px-3 pb-3 shrink-0">
          <WorkspaceCard {...workspace} />
        </div>
      ) : null}

      {hasScrollable ? (
        <>
          <div className="shrink-0 px-2 pt-3 pb-2 space-y-4">
            {fixedGroups.map((group, idx) => (
              <Group key={group.heading ?? `f-${idx}`} group={group} activePath={activePath} />
            ))}
            {scrollableGroup?.heading ? (
              <div className="px-2.5 flex items-center justify-between text-[11px] uppercase tracking-wider text-foreground-muted">
                <span>{scrollableGroup.heading}</span>
                {scrollableGroup.headingAction ? <div>{scrollableGroup.headingAction}</div> : null}
              </div>
            ) : null}
          </div>
          <nav className="flex-1 min-h-0 px-2 pb-2 overflow-y-auto">
            {scrollableGroup
              ? (scrollableGroup.renderContent ?? (
                  <ItemList items={scrollableGroup.items} activePath={activePath} />
                ))
              : null}
          </nav>
        </>
      ) : (
        <nav className="flex-1 min-h-0 px-2 pt-3 pb-2 space-y-4 overflow-y-auto">
          {groups.map((group, idx) => (
            <Group key={group.heading ?? `g-${idx}`} group={group} activePath={activePath} />
          ))}
        </nav>
      )}

      {utilityLinks && utilityLinks.length > 0 ? (
        <div className="shrink-0 px-2 pb-2 space-y-0.5">
          {utilityLinks.map((link) => {
            const Icon = link.icon;
            const className =
              'flex items-center gap-2.5 h-9 px-2.5 rounded-md text-sm text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors';
            if (link.to) {
              return (
                <Link key={link.to} to={link.to} className={className}>
                  <Icon className="size-4 shrink-0" />
                  <span className="truncate">{link.label}</span>
                </Link>
              );
            }
            const href = link.href ?? '#';
            return (
              <a
                key={href}
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className={className}
              >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">{link.label}</span>
              </a>
            );
          })}
        </div>
      ) : null}

      {footer ? <div className="shrink-0 border-t border-border p-2">{footer}</div> : null}
    </aside>
  );
}

function Group({ group, activePath }: { group: SidebarGroup; activePath: string }) {
  const [open, setOpen] = useState(group.defaultOpen ?? true);

  const content = group.renderContent ?? <ItemList items={group.items} activePath={activePath} />;

  if (group.collapsible) {
    return (
      <div>
        {group.heading ? (
          <div className="flex items-center justify-between px-2.5 py-1">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-foreground-muted hover:text-foreground"
            >
              <span>{group.heading}</span>
              {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            </button>
            {group.headingAction ? <div className="ml-1">{group.headingAction}</div> : null}
          </div>
        ) : null}
        {open ? content : null}
      </div>
    );
  }

  return (
    <div>
      {group.heading ? (
        <div className="px-2.5 mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-foreground-muted">
          <span>{group.heading}</span>
          {group.headingAction ? <div>{group.headingAction}</div> : null}
        </div>
      ) : null}
      {content}
    </div>
  );
}

function ItemList({ items, activePath }: { items: SidebarItem[]; activePath: string }) {
  // 한 그룹 안에서 activePath 에 매치되는 후보 중 가장 긴 path 만 active.
  // (예: /admin/wikis/config 에 있을 때 '/admin/wikis' prefix 매치보다 '/admin/wikis/config' 정확 매치가 우선)
  const matchedPath = items
    .map((i) => i.to)
    .filter((p): p is string => p !== undefined)
    .filter(
      (p) => activePath === p || (p !== '/admin' && p !== '/' && activePath.startsWith(`${p}/`)),
    )
    .sort((a, b) => b.length - a.length)[0];

  return (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const isActive = item.to !== undefined && item.to === matchedPath;
        const Icon = item.icon;
        const itemKey = item.to ?? item.label;
        const linkClass = cn(
          'flex items-center gap-2.5 h-9 rounded-md text-sm transition-colors w-full text-left',
          item.action ? 'pl-2.5 pr-8' : 'px-2.5',
          isActive
            ? 'bg-surface text-foreground'
            : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        );
        const inner = (
          <>
            {Icon ? <Icon className="size-4 shrink-0" /> : null}
            <span className="truncate flex-1">{item.label}</span>
            {item.shortcut ? (
              <kbd className="shrink-0 text-[10px] font-medium font-sans px-1.5 py-0.5 rounded-sm border border-border bg-surface-muted text-foreground-muted">
                {item.shortcut}
              </kbd>
            ) : null}
            {item.badge ? (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-badge-new text-white">
                {item.badge}
              </span>
            ) : null}
          </>
        );
        return (
          <li key={itemKey} className="group relative" data-active={isActive || undefined}>
            {isActive ? (
              <span
                className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-full bg-accent"
                aria-hidden
              />
            ) : null}
            {item.renderOverride ? (
              <div
                className={cn(
                  'flex items-center gap-2.5 h-9 px-2.5 rounded-md text-sm',
                  isActive ? 'bg-surface' : 'bg-surface-muted/40',
                )}
              >
                {Icon ? <Icon className="size-4 shrink-0 text-foreground-muted" /> : null}
                <div className="flex-1 min-w-0">{item.renderOverride}</div>
              </div>
            ) : (
              <>
                {item.to !== undefined ? (
                  <Link to={item.to} className={linkClass}>
                    {inner}
                  </Link>
                ) : (
                  <button type="button" onClick={item.onClick} className={linkClass}>
                    {inner}
                  </button>
                )}
                {item.action ? (
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-data-[active]:opacity-100 focus-within:opacity-100 transition-opacity">
                    {item.action}
                  </div>
                ) : null}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function WorkspaceCard({ name, selectable = false }: WorkspaceProps) {
  return (
    <button
      type="button"
      disabled={!selectable}
      className={cn(
        'w-full h-11 px-3 rounded-md bg-surface-strong flex items-center gap-2.5 text-left',
        selectable ? 'hover:bg-surface-muted transition-colors' : 'cursor-default',
      )}
    >
      <span className="size-5 rounded-sm bg-accent/15 text-accent grid place-items-center">
        <span className="size-2 rounded-[2px] bg-accent" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{name}</span>
      </span>
      {selectable ? <ChevronDown className="size-3.5 text-foreground-muted" /> : null}
    </button>
  );
}
